import React, { useState } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import LoginScreen from "@/auth/LoginScreen";
import MdqAnalyzer from "@/components/MdqAnalyzer";
import MdqHistory from "@/components/MdqHistory";
import { cn } from "@/lib/utils";
import { Loader2, LogOut, Shield, User as UserIcon, History } from "lucide-react";

function AppShell() {
  const { user, logout } = useAuth();
  const [view, setView] = useState("analyzer"); // analyzer | history
  const [openRun, setOpenRun] = useState(null);
  const isAdmin = user?.role === "admin";

  return (
    <div className="App min-h-screen bg-valantic-bg">
      <Toaster position="top-right" richColors />

      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-valantic-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-sm val-gradient-warm text-white flex items-center justify-center shadow-sm">
              <ValHex size={18} />
            </div>
            <div className="flex flex-col leading-tight">
              <div className="font-display font-bold text-sm text-valantic-black tracking-tight flex items-center gap-1.5">
                <span className="font-display font-bold lowercase tracking-tight">valantic</span>
                <span className="text-valantic-muted font-normal">·</span>
                <span className="font-medium">Master Data Quality Analyzer</span>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-valantic-muted">
                SAP IBP / PP/DS · powered by AI
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setView(view === "history" ? "analyzer" : "history")}
              data-testid="history-nav-btn"
              className={cn(
                "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm text-xs transition-colors",
                view === "history"
                  ? "bg-valantic-black text-white border border-valantic-black"
                  : "border border-valantic-border bg-white hover:bg-slate-50 text-slate-700"
              )}
            >
              <History className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span className="hidden sm:inline">{isAdmin ? "All analyses" : "History"}</span>
            </button>
            <div className="hidden sm:flex items-center gap-2 px-2.5 h-8 rounded-sm border border-valantic-border bg-white" data-testid="user-chip">
              {isAdmin
                ? <Shield className="w-3.5 h-3.5 text-emerald-600" strokeWidth={1.75} />
                : <UserIcon className="w-3.5 h-3.5 text-valantic-muted" strokeWidth={1.75} />}
              <div className="leading-tight">
                <div className="text-xs font-medium text-valantic-black truncate max-w-[180px]">{user?.email}</div>
                <div className="font-mono text-[9px] uppercase tracking-wider text-valantic-muted">
                  {user?.role}{isAdmin && " · all analyses"}
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-sm border border-valantic-border bg-white hover:bg-slate-50 text-xs text-slate-700"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {view === "analyzer" && (
        <MdqAnalyzer onOpenHistory={() => setView("history")} />
      )}
      {view === "history" && (
        <div className="px-4 md:px-6 py-6 md:py-8">
          <MdqHistory
            onBack={() => { setView("analyzer"); setOpenRun(null); }}
            onOpenRun={(run) => { /* opening past run = brief modal could be added later */ }}
          />
        </div>
      )}

      <footer className="mt-12 py-6 border-t border-valantic-border bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-valantic-muted">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-valantic-black lowercase tracking-tight">valantic</span>
            <span className="text-valantic-silver">·</span>
            <span className="font-mono uppercase tracking-wider">Master Data Quality Analyzer v3.0</span>
          </div>
          <div className="text-[11px]">SAP IBP / PP/DS · AI-powered · Per-user vault</div>
          <div className="font-mono text-[10px] uppercase tracking-wider">© {new Date().getFullYear()} valantic</div>
        </div>
      </footer>
    </div>
  );
}

const ValHex = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M16 2.5 27.5 9v14L16 29.5 4.5 23V9L16 2.5Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M16 9 22 12.5v7L16 23l-6-3.5v-7L16 9Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" opacity="0.7" />
    <circle cx="16" cy="16" r="2" fill="white" />
  </svg>
);

function GatedApp() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-valantic-bg flex items-center justify-center">
        <div className="flex items-center gap-3 text-valantic-muted">
          <Loader2 className="w-5 h-5 ibp-spin-slow" strokeWidth={1.5} />
          <span className="text-sm font-mono">Verifying session…</span>
        </div>
      </div>
    );
  }
  if (!user) return <LoginScreen />;
  return <AppShell />;
}

export default function App() {
  return (
    <AuthProvider>
      <GatedApp />
    </AuthProvider>
  );
}
